// src/components/AutoLogout.js
import { useEffect, useRef } from "react";
import Swal from "sweetalert2";

/**
 * AutoLogout (cross-tab)
 * - Put this once at the root of the app (e.g. in App.js)
 * - It updates `localStorage.lastActivity` on user events (mouse/keyboard/touch/visibility)
 * - On inactivity > timeoutMs it shows a SweetAlert and then runs logout
 *
 * Props:
 *   timeoutMs: number (default 2 hours)
 *   onBeforeLogout: optional callback run BEFORE clearing storage and redirecting (for server-side logout)
 *   redirectPath: where to go after logout (default "/login")
 */
export default function AutoLogout({
  timeoutMs = 2 * 60 * 60 * 1000,
  onBeforeLogout = null,
  redirectPath = "/login",
}) {
  const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
  const checkIntervalMs = 30 * 1000; // check every 30s
  const timerRef = useRef(null);

  // update lastActivity in localStorage (shared across tabs)
  const refreshActivity = () => {
    try {
      localStorage.setItem("lastActivity", Date.now().toString());
    } catch (e) {
      // ignore storage errors
    }
  };

  // central logout routine (shared by manual and auto)
  const doLogout = async (reason = "inactive") => {
    try {
      // optional hook to call backend logout / audit
      if (typeof onBeforeLogout === "function") {
        try {
          await onBeforeLogout(reason);
        } catch (e) {
          // ignore errors in hook
          console.error("onBeforeLogout error:", e);
        }
      }
    } catch (e) {}

    // show alert then clear and redirect
    await Swal.fire({
      title: "Logged out",
      text: reason === "manual" ? "You have been logged out." : "You were logged out due to inactivity.",
      icon: "info",
      confirmButtonText: "OK",
      allowOutsideClick: false,
      allowEscapeKey: false,
    });

    try {
      // clear session/local keys used by your app
      localStorage.removeItem("lastActivity");
      localStorage.setItem("logout", Date.now().toString()); // notify other tabs
      sessionStorage.removeItem("ldata");
      sessionStorage.removeItem("lastActivity"); // if used
    } catch (e) {}

    // redirect to login (adjust if your login route is different)
    window.location.href = redirectPath;
  };

  // Expose manual trigger in localStorage so other components can call logout
  // Use localStorage key "triggerLogout" with any value; this component listens and executes.
  useEffect(() => {
    const handleTrigger = (ev) => {
      // listen for cross-tab logout signal
      if (!ev) return;
      if (ev.key === "triggerLogout" && ev.newValue) {
        // manual logout triggered from other tab
        doLogout("manual");
      }
      if (ev.key === "logout" && ev.newValue) {
        // another tab already logged out; show modal and redirect
        // we can just redirect without showing modal to avoid duplicate modals:
        try {
          sessionStorage.removeItem("ldata");
          sessionStorage.removeItem("lastActivity");
        } catch (e) {}
        window.location.href = redirectPath;
      }
    };
    window.addEventListener("storage", handleTrigger);
    return () => window.removeEventListener("storage", handleTrigger);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // attach activity listeners
    const handleActivity = () => refreshActivity();
    events.forEach((ev) => window.addEventListener(ev, handleActivity, { passive: true }));

    // visibility change: treat as activity when tab becomes visible
    const handleVisibility = () => {
      if (!document.hidden) refreshActivity();
    };
    document.addEventListener("visibilitychange", handleVisibility);

    // ensure lastActivity exists
    if (!localStorage.getItem("lastActivity")) refreshActivity();

    // interval check
    timerRef.current = setInterval(() => {
      try {
        const last = localStorage.getItem("lastActivity");
        if (!last) return;
        const lastTs = parseInt(last, 10);
        if (isNaN(lastTs)) {
          refreshActivity();
          return;
        }
        const now = Date.now();
        if (now - lastTs > timeoutMs) {
          // fire logout routine and stop further checks
          clearInterval(timerRef.current);
          doLogout("inactive");
        }
      } catch (e) {
        // ignore
      }
    }, checkIntervalMs);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, handleActivity));
      document.removeEventListener("visibilitychange", handleVisibility);
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeoutMs]);

  return null;
}
